import React from 'react';
import cn from 'classnames';

import AlertIcon from 'widget/shared/icons/AlertIcon';
import CrossIcon from 'widget/shared/icons/CrossIcon';
import { IconColor, IconSize } from 'widget/shared/icons/BaseIcon';
import Typography from 'widget/shared/ui/Typography';
import {
  TypographySize,
  TypographyTag
} from 'widget/shared/ui/Typography/types';
import { WidgetTexts } from 'widget/consts/texts';

import IconButton from '../IconButton';

import styles from './ErrorToast.module.scss';

export type ErrorToastProps = {
  message: string;
  onClose: () => void;
};

const ErrorToast: React.FC<ErrorToastProps> = ({ message, onClose }) => {
  const [isPaused, setIsPaused] = React.useState(false);
  const slideInDoneRef = React.useRef(false);

  const handleAnimationEnd = React.useCallback(() => {
    if (!slideInDoneRef.current) {
      slideInDoneRef.current = true;
      return;
    }

    onClose();
  }, [onClose]);

  const handleMouseEnter = React.useCallback(() => setIsPaused(true), []);
  const handleMouseLeave = React.useCallback(() => setIsPaused(false), []);

  const handleClose = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onClose();
    },
    [onClose]
  );

  return (
    <div
      className={cn(styles['toast'], isPaused && styles['toast_paused'])}
      onAnimationEnd={handleAnimationEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <AlertIcon color={IconColor.Error} size={IconSize.SM} />
      <Typography
        tag={TypographyTag.span}
        size={TypographySize.xs}
        className={styles['toast__text']}
      >
        {message}
      </Typography>
      <IconButton
        className={styles['toast__close']}
        onClick={handleClose}
        aria-label={WidgetTexts.shared.closeButton}
      >
        <CrossIcon size={IconSize.XS} color={IconColor.Error} />
      </IconButton>
    </div>
  );
};

export default React.memo(ErrorToast);
